export const NET_PROTOCOL_VERSION = 3

export type PlayerId = string
export type TeamId = 'ffa'
export type HitGroup = 'head' | 'chest' | 'stomach' | 'leftLeg' | 'rightLeg'

/** Every weapon the game supports (no scoped/sniper rifles). 'ak' = AK-47. */
export type WeaponKind =
  | 'knife'
  | 'ak'
  | 'm4a1'
  | 'mp5'
  | 'tmp'
  | 'p90'
  | 'mac10'
  | 'm3'
  | 'xm1014'
  | 'm249'
  | 'glock'
  | 'usp'
  | 'deagle'
  | 'p228'
  | 'elite'
  | 'fiveseven'

/** Combat stats shared by the authoritative server and the client. Rendering
 * config (model urls, sounds, view-model placement) lives in the client-only
 * weapons registry, keyed by the same WeaponKind. Values approximate CS 1.6. */
export interface WeaponStats {
  melee: boolean
  damage: number // per-shot body damage (knife handled separately)
  fireInterval: number // seconds between shots
  clip: number
  reserve: number
  reloadSeconds: number
  automatic: boolean // hold-to-fire vs. one shot per press
}

export const WEAPON_STATS: Record<WeaponKind, WeaponStats> = {
  knife: { melee: true, damage: 0, fireInterval: 0.4, clip: 0, reserve: 0, reloadSeconds: 0, automatic: false },
  ak: { melee: false, damage: 36, fireInterval: 0.0955, clip: 30, reserve: 90, reloadSeconds: 2.4, automatic: true },
  m4a1: { melee: false, damage: 33, fireInterval: 0.09, clip: 30, reserve: 90, reloadSeconds: 3.0, automatic: true },
  mp5: { melee: false, damage: 26, fireInterval: 0.08, clip: 30, reserve: 120, reloadSeconds: 2.6, automatic: true },
  tmp: { melee: false, damage: 20, fireInterval: 0.07, clip: 30, reserve: 120, reloadSeconds: 2.1, automatic: true },
  p90: { melee: false, damage: 21, fireInterval: 0.0666, clip: 50, reserve: 100, reloadSeconds: 3.4, automatic: true },
  mac10: { melee: false, damage: 29, fireInterval: 0.075, clip: 30, reserve: 100, reloadSeconds: 3.1, automatic: true },
  // Shotguns: one ray per shot (no pellet spread in this model), so damage is
  // bumped to feel like a shotgun at close range.
  m3: { melee: false, damage: 75, fireInterval: 0.88, clip: 8, reserve: 32, reloadSeconds: 0.5, automatic: false },
  xm1014: { melee: false, damage: 55, fireInterval: 0.25, clip: 7, reserve: 32, reloadSeconds: 0.5, automatic: false },
  m249: { melee: false, damage: 32, fireInterval: 0.08, clip: 100, reserve: 200, reloadSeconds: 4.7, automatic: true },
  glock: { melee: false, damage: 20, fireInterval: 0.15, clip: 20, reserve: 120, reloadSeconds: 2.2, automatic: false },
  usp: { melee: false, damage: 34, fireInterval: 0.15, clip: 12, reserve: 100, reloadSeconds: 2.7, automatic: false },
  deagle: { melee: false, damage: 54, fireInterval: 0.22, clip: 7, reserve: 35, reloadSeconds: 2.2, automatic: false },
  p228: { melee: false, damage: 32, fireInterval: 0.15, clip: 13, reserve: 52, reloadSeconds: 2.7, automatic: false },
  elite: { melee: false, damage: 36, fireInterval: 0.12, clip: 30, reserve: 120, reloadSeconds: 4.5, automatic: false },
  fiveseven: { melee: false, damage: 20, fireInterval: 0.15, clip: 20, reserve: 100, reloadSeconds: 2.7, automatic: false },
}

export interface Vec3 {
  x: number
  y: number
  z: number
}

export interface PlayerInput {
  seq: number
  clientTime: number
  dt: number
  pos: Vec3
  vel: Vec3
  forward: number
  right: number
  jump: boolean
  duck: boolean
  walk: boolean
  yaw: number
  pitch: number
  fire: boolean
  altFire: boolean
  reload: boolean
  weapon: WeaponKind
  shotOrigin?: Vec3
  shotDir?: Vec3
  shotMaxDist?: number // distance to the first wall (server can't see geometry)
}

export interface ClientHello {
  version: typeof NET_PROTOCOL_VERSION
  // Stable per-browser id (localStorage). The server keys players by it so a
  // reconnect reclaims the same player/state instead of spawning a new one.
  sessionId: string
  name?: string
}

export interface ClientSpawn {
  pos: Vec3
  yaw: number
  pitch: number
}

export interface ClientWeaponDrop {
  weapon: WeaponKind
  pos: Vec3
  vel: Vec3
  yaw: number
  ammo: number // clip the weapon carried when dropped
  reserve: number // reserve the weapon carried when dropped
}

/** A player grabbed a weapon off the floor. ammo/reserve carry the floor item's
 * remaining rounds (a dropped weapon keeps its bullets; map spawns send full),
 * so the authoritative server adopts them instead of granting a fresh loadout. */
export interface ClientWeaponPickup {
  weapon: WeaponKind
  ammo: number
  reserve: number
}

export interface ServerWelcome {
  id: PlayerId
  tickRate: number
  snapshotRate: number
  serverTime: number
  reconciliation: true
  interpolationDelayMs: number
  lagCompensation: true
}

export interface PlayerSnapshot {
  id: PlayerId
  team: TeamId
  pos: Vec3
  vel: Vec3
  yaw: number
  pitch: number
  health: number
  armor: number
  weapon: WeaponKind
  ammo: number
  reserve: number
  reloading: boolean
  ducking: boolean
  alive: boolean
  ping: number
}

export interface WorldSnapshot {
  serverTime: number
  tick: number
  selfId: PlayerId
  lastProcessedInputSeq: number
  players: PlayerSnapshot[]
  events: NetEvent[]
}

export interface ShotEvent {
  type: 'shot'
  shooterId: PlayerId
  weapon: WeaponKind
  origin: Vec3
  dir: Vec3
}

export interface HitEvent {
  type: 'hit'
  shooterId: PlayerId
  targetId: PlayerId
  damage: number
  health: number
  hitGroup: HitGroup
  headshot: boolean
  point: Vec3
  dir: Vec3
}

export interface FootstepEvent {
  type: 'footstep'
  playerId: PlayerId
  pos: Vec3
}

export interface ReloadEvent {
  type: 'reload'
  playerId: PlayerId
}

export interface DeathEvent {
  type: 'death'
  playerId: PlayerId
  killerId: PlayerId
  point: Vec3
  dir: Vec3
  headshot: boolean
}

export interface SpawnEvent {
  type: 'spawn'
  playerId: PlayerId
  pos: Vec3
}

export interface WeaponDropEvent {
  type: 'weapon_drop'
  playerId: PlayerId
  weapon: WeaponKind
  pos: Vec3
  vel: Vec3
  yaw: number
  ammo: number // rounds the dropped weapon carries (so remotes show/grant them)
  reserve: number
}

export type NetEvent =
  | ShotEvent
  | HitEvent
  | FootstepEvent
  | ReloadEvent
  | DeathEvent
  | SpawnEvent
  | WeaponDropEvent

export type ClientToServerMessage =
  | { type: 'hello'; data: ClientHello }
  | { type: 'spawn'; data: ClientSpawn }
  | { type: 'weaponDrop'; data: ClientWeaponDrop }
  | { type: 'weaponPickup'; data: ClientWeaponPickup }
  | { type: 'input'; data: PlayerInput }
  | { type: 'ping'; clientTime: number; rttMs?: number }

export type ServerToClientMessage =
  | { type: 'welcome'; data: ServerWelcome }
  | { type: 'snapshot'; data: WorldSnapshot }
  | { type: 'pong'; clientTime: number; serverTime: number }
  | { type: 'disconnect'; reason: string }
